import { useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import type L from 'leaflet';
import { MapPin, Clock, Calendar, Globe, ChevronRight, ChevronDown, Music, Pencil, Trash2, Check, X } from 'lucide-react';
import axios from '../api/axios';
import { describeCron } from '../utils/cronDescription';

export interface SubmittedEvent {
  id: string;
  title: string;
  description: string;
  location: string;
  lat: number;
  lng: number;
  imageUrl: string | null;
  url: string;
  free: boolean;
  recurring?: boolean;
  recurrencePattern?: string | null;
  recurrenceCron?: string | null;
  recurrenceStartDate?: string | null;
  recurrenceEndDate?: string | null;
  startTime: string;
  endTime: string;
  tags: string[];
  submittedAt: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  email?: string | null;
  submissionId?: string | null;
  ticketUrl?: string | null;
}

interface EditData {
  title: string;
  location: string;
  lat: number;
  lng: number;
  description: string;
  startTime: string;
  endTime: string;
  url: string;
  free: boolean;
  recurring: boolean;
  recurrencePattern: string;
  recurrenceCron: string;
  recurrenceStartDate: string;
  recurrenceEndDate: string;
  tags: string;
  imageUrl: string;
}

interface SubmittedEventPopupProps {
  event: SubmittedEvent;
  onApprove: (id: string, recurrenceCron?: string) => void;
  onReject: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (id: string, data: Partial<SubmittedEvent>) => Promise<void>;
  onOpenImagePreview?: (imageUrl: string, title: string) => void;
}

function toDatetimeLocal(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const statusLabel: Record<SubmittedEvent['status'], string> = {
  PENDING: 'Oczekuje',
  APPROVED: 'Zatwierdzone',
  REJECTED: 'Odrzucone',
};

const statusColor: Record<SubmittedEvent['status'], string> = {
  PENDING: '#f59e0b',
  APPROVED: '#22c55e',
  REJECTED: '#ef4444',
};

async function reverseGeocodeLocation(lat: number, lng: number): Promise<string | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
    );
    if (!res.ok) return null;
    const data = await res.json();
    return (data?.display_name as string | undefined) ?? null;
  } catch {
    return null;
  }
}

export default function SubmittedEventPopup({ event, onApprove, onReject, onDelete, onEdit, onOpenImagePreview }: SubmittedEventPopupProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<EditData>({
    title: event.title,
    location: event.location,
    lat: event.lat,
    lng: event.lng,
    description: event.description,
    startTime: toDatetimeLocal(event.startTime),
    endTime: toDatetimeLocal(event.endTime),
    url: event.url,
    free: event.free,
    recurring: event.recurring ?? false,
    recurrencePattern: event.recurrencePattern ?? '',
    recurrenceCron: event.recurrenceCron ?? '',
    recurrenceStartDate: event.recurrenceStartDate ? toDatetimeLocal(event.recurrenceStartDate) : '',
    recurrenceEndDate: event.recurrenceEndDate ? toDatetimeLocal(event.recurrenceEndDate) : '',
    tags: event.tags.join(', '),
    imageUrl: event.imageUrl ?? '',
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const DESCRIPTION_MAX_CHARS = 100;
  const isDescriptionTruncated = (event.description?.length ?? 0) > DESCRIPTION_MAX_CHARS;

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setEditData(d => ({ ...d, imageUrl: '' }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImageFile = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleMarkerDragEnd = async (e: L.LeafletEvent) => {
    const marker = e.target as L.Marker;
    const { lat, lng } = marker.getLatLng();
    setEditData(d => ({ ...d, lat, lng }));
    const addr = await reverseGeocodeLocation(lat, lng);
    if (addr) setEditData(d => ({ ...d, location: addr }));
  };

  const uploadImageToS3 = async (): Promise<string | null> => {
    if (!imageFile) return editData.imageUrl || null;
    setIsUploadingImage(true);
    try {
      const payload = new FormData();
      payload.append('file', imageFile);
      payload.append('type', 'submission');
      const response = await axios.post('/upload', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return (response.data?.url as string | undefined) ?? null;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Nie udało się wysłać zdjęcia do S3');
      throw error;
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleApprove = async () => {
    let cronForApproval: string | undefined;
    if (event.recurring && !event.recurrenceCron) {
      const entered = window.prompt('To zgłoszenie jest cykliczne i nie ma jeszcze ustawionego wyrażenia cron. Podaj wyrażenie cron, aby zatwierdzić zgłoszenie:');
      if (!entered || !entered.trim()) {
        alert('Wyrażenie cron jest wymagane, aby zatwierdzić cykliczne zgłoszenie.');
        return;
      }
      cronForApproval = entered.trim();
    }
    setBusy(true);
    try {
      if (cronForApproval) {
        await onApprove(event.id, cronForApproval);
      } else {
        await onApprove(event.id);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    setBusy(true);
    try { await onReject(event.id); } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    setBusy(true);
    try { await onDelete(event.id); } finally { setBusy(false); }
  };

  const handleSave = async () => {
    if (editData.recurring && editData.recurrenceCron && editData.recurrencePattern) {
      alert('Podaj albo wyrażenie cron, albo słowny opis cyklu — nie oba naraz.');
      return;
    }
    setBusy(true);
    try {
      const uploadedImageUrl = await uploadImageToS3();
      await onEdit(event.id, {
        title: editData.title,
        location: editData.location,
        lat: editData.lat,
        lng: editData.lng,
        description: editData.description,
        startTime: new Date(editData.startTime).toISOString(),
        endTime: new Date(editData.endTime).toISOString(),
        url: editData.url,
        free: editData.free,
        recurring: editData.recurring,
        recurrencePattern: editData.recurring ? (editData.recurrencePattern || null) : null,
        recurrenceCron: editData.recurring ? (editData.recurrenceCron || null) : null,
        recurrenceStartDate: editData.recurring && editData.recurrenceStartDate ? new Date(editData.recurrenceStartDate).toISOString() : null,
        recurrenceEndDate: editData.recurring && editData.recurrenceEndDate ? new Date(editData.recurrenceEndDate).toISOString() : null,
        tags: editData.tags.split(',').map(t => t.trim()).filter(Boolean),
        imageUrl: uploadedImageUrl,
      });
      setIsEditing(false);
      clearImageFile();
    } catch {
      // errors are surfaced via alert inside uploadImageToS3
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="submitted-event-popup-root" className="sep-popup" onClick={e => e.stopPropagation()}>
      {isEditing ? (
        <>
          <p className="uppercase tracking-wide !font-[900] !text-base !text-slate-100 mb-2">Edycja zgłoszenia</p>

          <label className="modal-label">Tytuł:</label>
          <input className="modal-input" value={editData.title} onChange={e => setEditData(d => ({ ...d, title: e.target.value }))} />

          <label id="submitted-event-popup-edit-description-label" className="modal-label">Opis:</label>
          <textarea id="submitted-event-popup-edit-description" className="modal-textarea" value={editData.description} onChange={e => setEditData(d => ({ ...d, description: e.target.value }))} />

          <label className="modal-label">Lokalizacja:</label>
          <input id="submitted-event-popup-edit-location" className="modal-input" value={editData.location} onChange={e => setEditData(d => ({ ...d, location: e.target.value }))} />

          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label className="modal-label">Długość geo.:</label>
              <input type="number" step="any" className="modal-input" value={editData.lng}
                onChange={e => setEditData(d => ({ ...d, lng: Number(e.target.value) }))} />
            </div>
            <div>
              <label className="modal-label">Szerokość geo.:</label>
              <input type="number" step="any" className="modal-input" value={editData.lat}
                onChange={e => setEditData(d => ({ ...d, lat: Number(e.target.value) }))} />
            </div>
          </div>

          <label className="modal-label">Lokalizacja (przeciągnij pinezkę):</label>
          <div id="submitted-event-popup-edit-map" className="h-[300px] rounded-lg overflow-hidden border border-white/20 mb-2">
            <MapContainer center={[editData.lat, editData.lng]} zoom={15} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution=""
              />
              <Marker
                position={[editData.lat, editData.lng]}
                draggable
                eventHandlers={{ dragend: handleMarkerDragEnd }}
              />
            </MapContainer>
          </div>
          <p className="text-xs text-slate-400 mb-2">
            Przeciągnij pinezkę na mapie — długość, szerokość i miejsce zaktualizują się automatycznie.
          </p>

          <label className="modal-checkbox-label">
            <input type="checkbox" checked={!!editData.recurring} onChange={e => setEditData(d => ({ ...d, recurring: e.target.checked }))} />
            <span className="modal-label !mb-0">Wydarzenie cykliczne</span>
          </label>

          {editData.recurring && (
            <div id="submitted-event-popup-edit-recurrence-details">
              <label htmlFor="submitted-event-popup-edit-recurrence-start-date" className="modal-label">Cykl obowiązuje od:</label>
              <input
                id="submitted-event-popup-edit-recurrence-start-date"
                type="datetime-local"
                className="modal-input"
                value={editData.recurrenceStartDate}
                onChange={e => setEditData(d => ({ ...d, recurrenceStartDate: e.target.value }))}
              />

              <label htmlFor="submitted-event-popup-edit-recurrence-end-date" className="modal-label mt-2">Cykl obowiązuje do (opcjonalnie):</label>
              <input
                id="submitted-event-popup-edit-recurrence-end-date"
                type="datetime-local"
                className="modal-input"
                value={editData.recurrenceEndDate}
                onChange={e => setEditData(d => ({ ...d, recurrenceEndDate: e.target.value }))}
              />

              <label htmlFor="submitted-event-popup-edit-recurrence-cron" className="modal-label mt-2">Wyrażenie cron (opcjonalnie):</label>
              <input
                id="submitted-event-popup-edit-recurrence-cron"
                type="text"
                className="modal-input disabled:opacity-50"
                disabled={!!editData.recurrencePattern}
                value={editData.recurrenceCron}
                onChange={e => setEditData(d => ({ ...d, recurrenceCron: e.target.value }))}
                placeholder="np. 0 9 * * 6"
              />
              {editData.recurrenceCron && describeCron(editData.recurrenceCron) && (
                <p className="mt-1 text-xs text-slate-300">{describeCron(editData.recurrenceCron)}</p>
              )}

              <label htmlFor="submitted-event-popup-edit-recurrence-pattern" className="modal-label mt-2">Cykl (słowny opis, opcjonalnie):</label>
              <input
                id="submitted-event-popup-edit-recurrence-pattern"
                type="text"
                className="modal-input disabled:opacity-50"
                disabled={!!editData.recurrenceCron}
                value={editData.recurrencePattern}
                onChange={e => setEditData(d => ({ ...d, recurrencePattern: e.target.value }))}
                placeholder="np. Każda sobota o godzinie 9.00"
              />
              <p className="mt-1 text-xs text-slate-300">Podaj wyrażenie cron albo słowny opis — nie oba naraz.</p>
            </div>
          )}

          {!editData.recurring && (
            <>
              <label className="modal-label">Czas rozpoczęcia:</label>
              <input id="submitted-event-popup-edit-start-time" type="datetime-local" className="modal-input" value={editData.startTime} onChange={e => setEditData(d => ({ ...d, startTime: e.target.value }))} />

              <label className="modal-label">Czas zakończenia:</label>
              <input id="submitted-event-popup-edit-end-time" type="datetime-local" className="modal-input" value={editData.endTime} onChange={e => setEditData(d => ({ ...d, endTime: e.target.value }))} />
            </>
          )}

          <label className="modal-label">Strona internetowa:</label>
          <input id="submitted-event-popup-edit-url" className="modal-input" value={editData.url} onChange={e => setEditData(d => ({ ...d, url: e.target.value }))} />

          <label className="modal-label" htmlFor="submitted-event-popup-edit-image-url">URL do zdjęcia poglądowego:</label>
          <input
            id="submitted-event-popup-edit-image-url"
            className="modal-input"
            type="url"
            placeholder="https://..."
            value={editData.imageUrl}
            onChange={e => {
              const value = e.target.value;
              setEditData(d => ({ ...d, imageUrl: value }));
              if (value) clearImageFile();
            }}
            disabled={!!imageFile}
          />
          <label className="modal-label" htmlFor="submitted-event-popup-edit-image-file">Zdjęcie poglądowe:</label>
          {/* Mutually exclusive with the image URL field above - only one image source is allowed. */}
          <input
            id="submitted-event-popup-edit-image-file"
            type="file"
            accept="image/*"
            ref={fileInputRef}
            className="modal-input"
            onChange={handleImageFileChange}
            disabled={!!editData.imageUrl}
          />
          {imagePreview && (
            <div id="submitted-event-popup-edit-image-preview-container" className="relative mt-2 w-full h-24">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full h-24 object-cover rounded"
              />
              <button
                type="button"
                onClick={clearImageFile}
                aria-label="Usuń zdjęcie"
                className="modal-close-btn absolute top-1 right-1 inline-flex h-6 w-6 items-center justify-center rounded-full border text-white"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                  <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          )}

          <label className="modal-label" htmlFor="submitted-event-popup-edit-tags">Tagi (przecinek jako separator):</label>
          <input id="submitted-event-popup-edit-tags" className="modal-input" value={editData.tags} onChange={e => setEditData(d => ({ ...d, tags: e.target.value }))} />

          <label className="modal-checkbox-label">
            <input type="checkbox" checked={editData.free} onChange={e => setEditData(d => ({ ...d, free: e.target.checked }))} />
            <span className="modal-label !mb-0">Wstęp wolny</span>
          </label>

          <div className="modal-btn-row">
            <button
              id="submitted-event-popup-cancel-edit"
              onClick={() => { setIsEditing(false); clearImageFile(); }}
              disabled={busy || isUploadingImage}
              className="btn-action btn-red"
            >
              Odrzuć
            </button>
            <button
              id="submitted-event-popup-save-edit"
              onClick={handleSave}
              disabled={busy || isUploadingImage}
              className="btn-action btn-green"
            >
              {isUploadingImage ? 'Wysyłanie zdjęcia...' : busy ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Status badge */}
          <div className="mb-1.5">
            <span className="sep-status-badge" style={{ backgroundColor: statusColor[event.status] }}>
              {statusLabel[event.status]}
            </span>
          </div>

          <div id="submitted-event-popup-header" className="mb-3 flex items-start gap-3">
            {event.imageUrl ? (
              <button
                id='submitted-event-popup-image-button'
                type="button"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  onOpenImagePreview?.(event.imageUrl!, event.title);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (e.detail === 0) {
                    onOpenImagePreview?.(event.imageUrl!, event.title);
                  }
                }}
                className="relative block h-14 w-14 shrink-0 cursor-zoom-in overflow-hidden rounded-full"
                aria-label="Powiększ zdjęcie"
              >
                <img
                  id='submitted-event-popup-image'
                  src={event.imageUrl}
                  alt={event.title}
                  className="h-14 w-14 rounded-full object-cover"
                />
                <span
                  id="submitted-event-popup-magnifying-glass-trigger"
                  className="pointer-events-none absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/80 bg-black/60 text-white"
                  aria-hidden="true"
                >
                  <svg width="10" height="10" viewBox="0 0 14 14" aria-hidden="true">
                    <line x1="7" y1="1" x2="7" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="1" y1="7" x2="13" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
              </button>
            ) : (
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/20 text-[var(--accent)]">
                <Music size={22} strokeWidth={2} aria-hidden="true" />
                <span className="sr-only">Brak fotografii</span>
              </span>
            )}
            <div className="min-w-0 flex-1 pt-0.5 text-left">
              <p id="submitted-event-popup-title" className="leading-tight">
                <span className="sr-only">Tytuł wydarzenia:</span>
                <span className="block text-lg font-bold text-white">{event.title}</span>
              </p>
              <div className="mt-0.5">
                <p
                  id="submitted-event-popup-description"
                  className={`text-sm leading-5 transition-colors duration-300 ease-in-out ${isDescriptionExpanded ? 'text-slate-100' : 'text-slate-400'}`}
                >
                  <span id="submitted-event-popup-description-label" className="sr-only">Opis:</span>
                  {isDescriptionExpanded || !isDescriptionTruncated
                    ? event.description
                    : `${event.description?.slice(0, DESCRIPTION_MAX_CHARS).trimEnd()}…`}
                </p>
                {isDescriptionTruncated && (
                  <button
                    type="button"
                    id="submitted-event-popup-description-toggle"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDescriptionExpanded((prev) => !prev);
                    }}
                    className="mt-1 flex items-center gap-1 text-xs font-semibold text-[var(--accent)] hover:text-[var(--accent-strong)]"
                  >
                    {isDescriptionExpanded ? 'Zwiń opis' : 'Rozwiń opis'}
                    <ChevronDown
                      size={14}
                      strokeWidth={2.5}
                      className={`transition-transform duration-300 ${isDescriptionExpanded ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="mb-3 border-t border-white/10" />

          {!event.free && (
            <div className="mb-3">
              <span className="sep-paid-badge">🎟 Wstęp płatny</span>
              {event.ticketUrl && (
                <p className="mt-1 text-sm text-slate-300">
                  <span className="uppercase tracking-wide !font-[900] !text-base !text-slate-100">Link do biletów:</span>{' '}
                  <a href={event.ticketUrl} target="_blank" rel="noopener noreferrer" className="popup-yellow-link break-all">
                    {event.ticketUrl}
                  </a>
                </p>
              )}
            </div>
          )}

          <div className="mb-3 space-y-3">
            <div id="submitted-event-popup-location" className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
                <MapPin size={16} strokeWidth={2.25} aria-hidden="true" />
              </span>
              <div className="min-w-0 text-left">
                <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Lokalizacja:</span>
                <span className="text-sm font-semibold text-white">{event.location}</span>
              </div>
            </div>

            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
                <Calendar size={16} strokeWidth={2.25} aria-hidden="true" />
              </span>
              <div className="min-w-0 text-left">
                <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">
                  {event.recurring ? 'Cykl:' : 'Termin:'}
                </span>
                {event.recurring ? (
                  <>
                    <p className="text-sm font-semibold text-white">
                      {event.recurrenceCron ? describeCron(event.recurrenceCron) : (event.recurrencePattern || 'Brak opisu cyklu')}
                    </p>
                    {(event.recurrenceStartDate || event.recurrenceEndDate) && (
                      <div className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-xs text-slate-200">
                        <Clock size={12} strokeWidth={2.5} aria-hidden="true" />
                        <span>{event.recurrenceStartDate ? formatDate(event.recurrenceStartDate) : '—'}</span>
                        <span aria-hidden="true">→</span>
                        <span>{event.recurrenceEndDate ? formatDate(event.recurrenceEndDate) : 'bez końca'}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-white">
                      {new Date(event.startTime).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                    <div className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-xs text-slate-200">
                      <Clock size={12} strokeWidth={2.5} aria-hidden="true" />
                      <span>{new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      <span aria-hidden="true">→</span>
                      <span>
                        {new Date(event.startTime).toDateString() === new Date(event.endTime).toDateString()
                          ? new Date(event.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : new Date(event.endTime).toLocaleString()}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {event.url ? (
              <a
                href={event.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Otwórz stronę internetową wydarzenia w nowej karcie"
                title="Otwórz stronę wydarzenia"
                className="-mx-1 flex items-center gap-2.5 rounded-lg px-1 py-0.5 hover:bg-white/5"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
                  <Globe size={16} strokeWidth={2.25} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Strona internetowa:</span>
                  <span className="popup-yellow-link block truncate text-sm font-semibold underline">{event.url}</span>
                </span>
                <ChevronRight size={16} className="shrink-0 text-slate-500" aria-hidden="true" />
              </a>
            ) : (
              <div className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent)]">
                  <Globe size={16} strokeWidth={2.25} aria-hidden="true" />
                </span>
                <div className="min-w-0 text-left">
                  <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-400">Strona internetowa:</span>
                  <span className="text-sm text-slate-300">Brak strony internetowej</span>
                </div>
              </div>
            )}
          </div>

          <div className="mb-3 border-t border-white/10" />

          {event.tags?.length > 0 && (
            <div className="mb-3">
              <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200 mb-1">Tagi:</span>
              <div id="submitted-event-popup-tags-row" className="sep-tags-row">
                {event.tags.map(tag => (
                  <span key={tag} className="sep-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {event.free && (
            <div className="mb-3">
              <span className="sep-free-badge">
                Wstęp wolny
              </span>
            </div>
          )}

          <p id="submitted-event-popup-submitted-at" className="mb-2 text-sm text-slate-300">
            <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Zgłoszono:</span>
            {formatDate(event.submittedAt)}
          </p>

          {event.email && (
            <p id="submitted-event-popup-email" className="mb-2 text-sm text-slate-300">
              <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-200">Email:</span>
              <a href={`mailto:${event.email}`} className="popup-email-link">
                {event.email}
              </a>
            </p>
          )}

          {event.submissionId && (
            <p className="mb-2 text-sm text-slate-300">
              <span className="uppercase tracking-wide !font-[900] !text-base !text-slate-100">ID zgłoszenia (przelew):</span>{' '}
              <span className="font-mono font-bold text-orange-300 tracking-widest">{event.submissionId}</span>
            </p>
          )}

          <div className="mb-3 border-t border-white/10" />

          {event.status === 'PENDING' && (
            <div className="space-y-2">
              <button
                id="submitted-event-popup-edit-submission"
                type="button"
                onClick={() => setIsEditing(true)}
                disabled={busy}
                className="btn-action btn-blue flex w-full items-center justify-center gap-1.5"
              >
                <Pencil size={14} strokeWidth={2.5} aria-hidden="true" />
                Edytuj
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={busy}
                  className="btn-action btn-red flex items-center justify-center gap-1.5"
                >
                  <X size={14} strokeWidth={2.5} aria-hidden="true" />
                  Odrzuć
                </button>
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={busy}
                  className="btn-action btn-green flex items-center justify-center gap-1.5"
                >
                  <Check size={14} strokeWidth={2.5} aria-hidden="true" />
                  Zapisz
                </button>
              </div>
            </div>
          )}
          {(event.status === 'REJECTED' || event.status === 'APPROVED') && (
            <div className="space-y-2">
              <button
                id="submitted-event-popup-edit-submission"
                type="button"
                onClick={() => setIsEditing(true)}
                disabled={busy}
                className="btn-action btn-blue flex w-full items-center justify-center gap-1.5"
              >
                <Pencil size={14} strokeWidth={2.5} aria-hidden="true" />
                Edytuj
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="btn-action btn-red flex w-full items-center justify-center gap-1.5"
              >
                <Trash2 size={14} strokeWidth={2.5} aria-hidden="true" />
                {busy ? 'Usuwanie...' : 'Usuń'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
